import React, { useState, useRef, useEffect } from "react";
import { Editor, Element as SlateElement, Transforms } from "slate";
import { useSlate } from "slate-react";
import { TableEditor } from "slate-table";
import { TableElement, TableRowElement, TableCellElement } from "../../types";
import { useTranslation } from "react-i18next";
import {
  ColumnInsertLeft,
  ColumnInsertRight,
  ColumnRemove,
  HeaderRow,
  RowInsertBottom,
  RowInsertTop,
  RowRemove,
} from "../elements/table/icons";

export const TableControls: React.FC = () => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const editor = useSlate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        event.target instanceof HTMLElement &&
        !menuRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isInTable = () => {
    const [match] = Editor.nodes(editor, {
      match: (n) =>
        SlateElement.isElement(n) && "type" in n && n.type === "table",
    });
    return !!match;
  };

  const insertTable = () => {
    // Use the TableEditor API to insert a table with 3 rows and 3 columns
    TableEditor.insertTable(editor, { rows: 3, cols: 3 });
  };

  const insertRowBelow = () => {
    TableEditor.insertRow(editor);
  };

  const insertRowAbove = () => {
    TableEditor.insertRow(editor, { before: true });
  };

  const removeRow = () => {
    TableEditor.removeRow(editor);
  };

  const insertColumnRight = () => {
    TableEditor.insertColumn(editor);
  };

  const insertColumnLeft = () => {
    TableEditor.insertColumn(editor, { before: true });
  };

  const removeColumn = () => {
    TableEditor.removeColumn(editor);
  };

  const deleteTable = () => {
    TableEditor.removeTable(editor);
    setShowMenu(false);
  };

  const toggleHeaderRow = () => {
    // Get the table node
    const [tableNode] = Editor.nodes(editor, {
      match: (n) =>
        SlateElement.isElement(n) && "type" in n && n.type === "table",
    });

    if (tableNode) {
      const [table, tablePath] = tableNode;

      // Get the first row
      if (table.children && table.children.length > 0) {
        const firstRow = table.children[0];
        const firstRowPath = [...tablePath, 0];

        // Check if the first row has header cells
        const hasHeaderCells = firstRow.children.some(
          (cell: TableCellElement) => cell.isHeader
        );

        // Toggle the header status for all cells in the first row
        firstRow.children.forEach((cell: TableCellElement, cellIndex) => {
          const cellPath = [...firstRowPath, cellIndex];
          Transforms.setNodes(
            editor,
            { isHeader: !hasHeaderCells },
            { at: cellPath }
          );
        });
      }
    }

    setShowMenu(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        className={`p-2 ${
          isInTable() ? "text-blue-500" : "text-gray-600"
        } hover:bg-gray-100 rounded`}
        onMouseDown={(event: React.MouseEvent) => {
          event.preventDefault();
          if (!isInTable()) {
            insertTable();
          } else {
            setShowMenu(!showMenu);
          }
        }}
        title={
          isInTable() ? t("table.tooltip.options") : t("table.tooltip.insert")
        }
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="h-6 w-6 text-gray-600"
        >
          <path
            fillRule="evenodd"
            d="M1.5 5.625c0-1.036.84-1.875 1.875-1.875h17.25c1.035 0 1.875.84 1.875 1.875v12.75c0 1.035-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 0 1 1.5 18.375V5.625ZM21 9.375A.375.375 0 0 0 20.625 9h-7.5a.375.375 0 0 0-.375.375v1.5c0 .207.168.375.375.375h7.5a.375.375 0 0 0 .375-.375v-1.5Zm0 3.75a.375.375 0 0 0-.375-.375h-7.5a.375.375 0 0 0-.375.375v1.5c0 .207.168.375.375.375h7.5a.375.375 0 0 0 .375-.375v-1.5Zm0 3.75a.375.375 0 0 0-.375-.375h-7.5a.375.375 0 0 0-.375.375v1.5c0 .207.168.375.375.375h7.5a.375.375 0 0 0 .375-.375v-1.5ZM10.875 18.75a.375.375 0 0 0 .375-.375v-1.5a.375.375 0 0 0-.375-.375h-7.5a.375.375 0 0 0-.375.375v1.5c0 .207.168.375.375.375h7.5ZM3.375 15h7.5a.375.375 0 0 0 .375-.375v-1.5a.375.375 0 0 0-.375-.375h-7.5a.375.375 0 0 0-.375.375v1.5c0 .207.168.375.375.375Zm0-3.75h7.5a.375.375 0 0 0 .375-.375v-1.5A.375.375 0 0 0 10.875 9h-7.5A.375.375 0 0 0 3 9.375v1.5c0 .207.168.375.375.375Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {showMenu && isInTable() && (
        <div className="absolute left-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1" role="menu">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
              onMouseDown={(e) => {
                e.preventDefault();
                insertRowAbove();
                setShowMenu(false);
              }}
            >
              {t("manual.table.actions.add_row_above")}
              <div className="flex-shrink-0 ml-2">
                <RowInsertTop height={20} width={20} />
              </div>
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
              onMouseDown={(e) => {
                e.preventDefault();
                insertRowBelow();
                setShowMenu(false);
              }}
            >
              {t("manual.table.actions.add_row_below")}
              <div className="flex-shrink-0 ml-2">
                <RowInsertBottom height={20} width={20} />
              </div>
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
              onMouseDown={(e) => {
                e.preventDefault();
                removeRow();
                setShowMenu(false);
              }}
            >
              {t("manual.table.actions.remove_row")}
              <div className="flex-shrink-0 ml-2">
                <RowRemove height={20} width={20} />
              </div>
            </button>
            <div className="border-t border-gray-200 my-1"></div>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
              onMouseDown={(e) => {
                e.preventDefault();
                insertColumnLeft();
                setShowMenu(false);
              }}
            >
              {t("manual.table.actions.add_column_left")}
              <div className="flex-shrink-0 ml-2">
                <ColumnInsertLeft height={20} width={20} />
              </div>
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
              onMouseDown={(e) => {
                e.preventDefault();
                insertColumnRight();
                setShowMenu(false);
              }}
            >
              {t("manual.table.actions.add_column_right")}
              <div className="flex-shrink-0 ml-2">
                <ColumnInsertRight height={20} width={20} />
              </div>
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
              onMouseDown={(e) => {
                e.preventDefault();
                removeColumn();
                setShowMenu(false);
              }}
            >
              {t("manual.table.actions.remove_column")}
              <div className="flex-shrink-0 ml-2">
                <ColumnRemove height={20} width={20} />
              </div>
            </button>
            {/* <div className="border-t border-gray-200 my-1"></div>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
              onMouseDown={(e) => {
                e.preventDefault();
                toggleHeaderRow();
              }}
            >
              {t("manual.table.actions.toggle_header_row", "Toggle Header Row")}
              <div className="flex-shrink-0 ml-2">
                <HeaderRow height={20} width={20} />
              </div>
            </button> */}
            <div className="border-t border-gray-200 my-1"></div>
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center justify-between"
              onMouseDown={(e) => {
                e.preventDefault();
                deleteTable();
              }}
            >
              {t("manual.table.actions.delete_table")}
              <div className="flex-shrink-0 ml-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
